<template>
  <!-- ═══════════════ Desktop (lg+): header · 4 stat cards · table + detail ═══════════════ -->
  <div v-if="isDesktop" class="dk">
    <div class="dk-body">
      <div class="dk-left">
        <!-- Heading + New asset, then search + filter, directly above the list. -->
        <header class="dk-head-row">
          <div class="dk-title-wrap">
            <h1 class="dk-title">Assets</h1>
            <span v-if="!loadingAssets" class="dk-count">{{ assets.length }}</span>
          </div>
          <v-btn v-if="can(Permissions.AssetCreate)" class="dk-add" variant="flat" size="small"
            prepend-icon="mdi-plus" @click="navigateToCreateAsset">New asset</v-btn>
        </header>

        <div class="dk-tools-row">
          <v-text-field v-model="searchQuery" class="dk-search" placeholder="Search assets" variant="outlined"
            density="compact" hide-details prepend-inner-icon="mdi-magnify" clearable @click:clear="searchQuery = ''">
            <template #append-inner>
              <kbd v-if="!searchQuery" class="dk-kbd">/</kbd>
            </template>
          </v-text-field>
          <v-menu v-if="availableSites.length" location="bottom end">
            <template #activator="{ props: menuProps }">
              <v-btn class="dk-sites" variant="flat" size="small" v-bind="menuProps"
                prepend-icon="mdi-map-marker" append-icon="mdi-chevron-down">{{ siteLabel }}</v-btn>
            </template>
            <v-list class="site-menu" density="compact">
              <v-list-item :active="!selectedSiteId" title="All sites" @click="selectedSiteId = null" />
              <v-list-item v-for="site in availableSites" :key="site.id" :active="selectedSiteId === site.id"
                :title="site.title" @click="selectedSiteId = site.id" />
            </v-list>
          </v-menu>
        </div>

        <div class="dk-list">
          <template v-if="loadingAssets">
            <div v-for="n in 6" :key="n" class="dk-row dk-row-skeleton">
              <span class="sk dk-sk-thumb"></span>
              <span class="dk-row-main">
                <span class="sk sk-line" style="width: 70%"></span>
                <span class="sk sk-line" style="width: 45%; height: 0.7rem"></span>
              </span>
            </div>
          </template>
        <template v-else>
          <button v-for="asset in filteredAssets" :key="asset.id" type="button" class="dk-row"
            :class="{ 'is-selected': selectedAsset && selectedAsset.id === asset.id }" @click="selectAsset(asset)">
            <span class="dk-thumb" :class="{ 'is-loading': thumbLoading(asset.id) }">
              <img v-if="imageMap[asset.id]" v-show="imgLoaded[asset.id]" :src="imageMap[asset.id]" :alt="asset.name"
                @load="imgLoaded[asset.id] = true" @error="onThumbError(asset.id)" />
              <v-icon v-else-if="thumbSettled[asset.id]" size="26" color="#4a5a60">mdi-image-outline</v-icon>
            </span>
            <span class="dk-row-main">
              <span class="dk-row-name">{{ asset.name }}</span>
              <span v-if="asset.siteTitle" class="dk-row-site">
                <v-icon size="12" color="#f0b54a">mdi-map-marker-outline</v-icon>{{ asset.siteTitle }}
              </span>
            </span>
          </button>
          <div v-if="!filteredAssets.length" class="list-state">
            <v-icon size="40" color="#4a5a60">{{ searchQuery || selectedSiteId ? 'mdi-magnify' : 'mdi-package-variant' }}</v-icon>
            <p>{{ searchQuery || selectedSiteId ? 'No matching assets' : 'No assets yet' }}</p>
          </div>
        </template>
        </div>
      </div>

      <AssetDetailPanel :asset="selectedAsset" :summary="selectedSummary" :image="detailImage"
        :actions-disabled="actionsDisabled" @action="handleDetailAction" @image-error="onDetailImageError" />
    </div>
  </div>

  <!-- ═══════════════ Tablet / mobile: master list (+ detail on md) ═══════════════ -->
  <div v-else class="assets-workspace" :class="{ 'has-detail': showDetail }">
    <section class="master">
      <header class="master-head">
        <h1 class="master-title">Assets</h1>
        <v-btn v-if="can(Permissions.AssetCreate)" class="add-btn" variant="outlined" size="small"
          prepend-icon="mdi-plus" @click="navigateToCreateAsset">Add</v-btn>
      </header>

      <div class="master-filters">
        <v-text-field v-model="searchQuery" class="master-search" placeholder="Search assets" variant="outlined"
          density="compact" hide-details prepend-inner-icon="mdi-magnify" clearable @click:clear="searchQuery = ''" />
        <v-menu v-if="availableSites.length" location="bottom end">
          <template #activator="{ props: menuProps }">
            <v-btn class="dk-sites" variant="flat" size="small" v-bind="menuProps"
              prepend-icon="mdi-map-marker" append-icon="mdi-chevron-down">{{ siteLabel }}</v-btn>
          </template>
          <v-list class="site-menu" density="compact">
            <v-list-item :active="!selectedSiteId" title="All sites" @click="selectedSiteId = null" />
            <v-list-item v-for="site in availableSites" :key="site.id" :active="selectedSiteId === site.id"
              :title="site.title" @click="selectedSiteId = site.id" />
          </v-list>
        </v-menu>
      </div>

      <div v-if="!showDetail" class="anomaly-summary">
        <div class="summary-headline">
          <span v-if="loadingBreakdown" class="sk sk-total"></span>
          <span v-else class="summary-total">{{ formatNumber(breakdown.total) }}</span>
          <span class="summary-caption">anomalies across all sites</span>
        </div>

        <template v-if="loadingBreakdown">
          <span class="sk sk-summary-bar"></span>
          <div class="summary-legend">
            <span class="sk sk-legend-line"></span>
            <span class="sk sk-legend-line"></span>
            <span class="sk sk-legend-line"></span>
          </div>
        </template>
        <template v-else>
          <div class="summary-bar" :aria-hidden="true">
            <span class="seg seg-pending" :style="{ width: pct(breakdown.pending) }"></span>
            <span class="seg seg-resolved" :style="{ width: pct(breakdown.accepted) }"></span>
            <span class="seg seg-critical" :style="{ width: pct(breakdown.rejected) }"></span>
          </div>
          <div class="summary-legend">
            <span class="legend"><span class="dot dot-pending"></span>{{ formatNumber(breakdown.pending) }} pending</span>
            <span class="legend"><span class="dot dot-resolved"></span>{{ formatNumber(breakdown.accepted) }} accepted</span>
            <span class="legend"><span class="dot dot-critical"></span>{{ formatNumber(breakdown.rejected) }} rejected</span>
          </div>
        </template>
      </div>

      <div class="asset-list">
        <template v-if="loadingAssets">
          <div v-for="n in 6" :key="n" class="asset-row asset-row-skeleton">
            <span class="sk row-sk-thumb"></span>
            <span class="row-main">
              <span class="sk sk-line" style="width: 65%"></span>
              <span class="sk sk-line" style="width: 40%; height: 0.7rem"></span>
            </span>
            <span class="sk row-sk-badge"></span>
          </div>
        </template>
        <template v-else>
          <!-- All assets (flat, ungrouped) -->
          <button v-for="asset in filteredAssets" :key="asset.id" type="button" class="asset-row"
            :class="{ 'is-selected': selectedAsset && selectedAsset.id === asset.id }" @click="selectAsset(asset)">
            <span class="row-thumb" :class="{ 'is-loading': thumbLoading(asset.id) }">
              <img v-if="imageMap[asset.id]" v-show="imgLoaded[asset.id]" :src="imageMap[asset.id]" :alt="asset.name"
                @load="imgLoaded[asset.id] = true" @error="onThumbError(asset.id)" />
              <v-icon v-else-if="thumbSettled[asset.id]" size="20" color="#4a5a60">mdi-image-outline</v-icon>
            </span>
            <span class="row-main">
              <span class="row-name">{{ asset.name }}</span>
              <span v-if="asset.siteTitle" class="row-site">{{ asset.siteTitle }}</span>
            </span>
            <span v-if="assetPending(asset.id) > 0" class="row-badge badge-pending">{{ assetPending(asset.id) }}</span>
            <span v-else class="row-badge" :class="anomalyBadgeClass(asset.id)">{{ anomalyBadgeText(asset.id) }}</span>
            <v-icon v-if="!showDetail" size="18" color="#5c6f78" class="row-chevron">mdi-chevron-right</v-icon>
          </button>

          <div v-if="!filteredAssets.length" class="list-state">
            <v-icon size="40" color="#4a5a60">{{ searchQuery || selectedSiteId ? 'mdi-magnify' : 'mdi-package-variant' }}</v-icon>
            <p>{{ searchQuery || selectedSiteId ? 'No matching assets' : 'No assets yet' }}</p>
          </div>
        </template>
      </div>
    </section>

    <AssetDetailPanel v-if="showDetail" :asset="selectedAsset" :summary="selectedSummary" :image="detailImage"
      :actions-disabled="actionsDisabled" @action="handleDetailAction" @image-error="onDetailImageError" />
  </div>

  <NewAssetDialog v-model="showNewAsset" :organization-id="organizationStore.organizationId"
    :sites="sites" @created="onAssetCreated" />
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import { useDisplay } from 'vuetify'

import AssetDetailPanel from '@/components/AssetDetailPanel.vue'
import NewAssetDialog from '@/components/NewAssetDialog.vue'
import AssetService from '@/services/AssetService'
import SiteService from '@/services/SiteService'
import DashboardDataService from '@/services/DashboardDataService'
import ModelVersionService from '@/services/ModelVersionService'
import organizationSasService from '@/services/OrganizationSasService'
import { useOrganizationStore } from '@/store/organizationStore'
import { useAssetStore } from '@/store/assetStore'
import { Permissions } from '@/constants/permissions'
import { usePermissions } from '@/composable/usePermissions'
import { SCAN_PROCESSING_SUCCESS } from '@/utils/ScanStatus'
import { pickCoverFrame } from '@/utils/coverFrame'

const router = useRouter()
const { can } = usePermissions()
const { mdAndUp, lgAndUp } = useDisplay()

const organizationStore = useOrganizationStore()
const assetStore = useAssetStore()
const { user } = useAuth0()

const assets = ref([])
const sites = ref([])
const loadingAssets = ref(true)
const breakdown = ref({ total: 0, pending: 0, accepted: 0, rejected: 0 })
const loadingBreakdown = ref(true)

const searchQuery = ref('')
const selectedSiteId = ref(null)
const showNewAsset = ref(false)

const imageMap = reactive({})        // assetId -> signed thumbnail url
const summaryMap = reactive({})      // assetId -> AssetSummaryViewModel (lazy)
const thumbTried = reactive({})      // assetId -> imagePath sign attempted
const frameTried = reactive({})      // assetId -> frame-fallback attempted
const imgLoaded = reactive({})       // assetId -> thumbnail <img> has decoded (drops the shimmer)
const thumbSettled = reactive({})    // assetId -> resolution finished with no image (show placeholder icon)

const selectedAsset = ref(null)
const detailImage = ref(null)
// Gate for the detail model/anomaly/dashboard actions — same rule as the asset landing page:
// disabled until every scan in the current model version has finished processing (Duallo admins
// bypass). True (disabled) by default until resolved for the selected asset.
const actionsDisabled = ref(true)

const isDesktop = lgAndUp             // full table + 4 stat cards + detail
const showDetail = mdAndUp           // two-column master/detail (tablet); single-column below

const UNASSIGNED = '__unassigned__'

const activeAssets = computed(() =>
  assets.value.filter(a => a.isActive === true || a.isactive === true)
)

const availableSites = computed(() => {
  const assignedIds = new Set(activeAssets.value.filter(a => a.siteId).map(a => a.siteId))
  const options = sites.value.filter(site => assignedIds.has(site.id)).map(s => ({ id: s.id, title: s.title }))
  if (activeAssets.value.some(a => !a.siteId)) options.push({ id: UNASSIGNED, title: 'Unassigned' })
  return options
})

const siteLabel = computed(() => {
  if (!selectedSiteId.value) return 'All sites'
  const s = availableSites.value.find(x => x.id === selectedSiteId.value)
  return s ? s.title : 'All sites'
})

const filteredAssets = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  let result = activeAssets.value
  if (selectedSiteId.value) {
    result = selectedSiteId.value === UNASSIGNED
      ? result.filter(a => !a.siteId)
      : result.filter(a => a.siteId === selectedSiteId.value)
  }
  if (query) {
    result = result.filter(a =>
      (a.name || '').toLowerCase().includes(query) ||
      (a.description || '').toLowerCase().includes(query))
  }
  return [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
})

const assetIds = computed(() => activeAssets.value.map(a => a.id))

// Per-asset pending count (mobile list badge); 0 until its summary loads.
function assetPending(assetId) {
  const s = summaryMap[assetId]
  return s && typeof s === 'object' ? Number(s.totalAnomaliesPending || 0) : 0
}

const selectedSummary = computed(() =>
  selectedAsset.value ? summaryMap[selectedAsset.value.id] : null)

onMounted(async () => {
  if (!user.value) return
  await Promise.all([loadAssets(), loadSites()])
  // The org anomaly summary lives in the sidebar on md+; only the mobile layout needs its own copy.
  if (!showDetail.value) loadBreakdown()
  // Re-select the asset the user was just viewing (3D model / findings / admin / landing) so returning
  // to Assets is uniform — desktop reopens its detail panel, mobile highlights + scrolls to the row.
  restoreLastAsset()
})

// Restore the last-viewed asset from the store (persisted across the asset sub-pages). Selection only —
// never navigates (so tapping the Assets tab on mobile doesn't bounce back into the asset).
function restoreLastAsset() {
  const lastId = assetStore.asset?.id
  if (!lastId) return
  const match = assets.value.find(a => a.id === lastId)
  if (!match) return
  selectedAsset.value = match
  if (showDetail.value) {
    detailImage.value = imageMap[match.id] || null
    resolveThumbnail(match)
    computeActionsDisabled(match)
    if (summaryMap[match.id] === undefined) {
      summaryMap[match.id] = null
      DashboardDataService.getModelVersionSummaryById(match.id)
        .then(summary => { summaryMap[match.id] = summary || false })
        .catch(() => { summaryMap[match.id] = false })
    }
  }
  nextTick(() => {
    document.querySelector('.dk-row.is-selected, .asset-row.is-selected')?.scrollIntoView({ block: 'nearest' })
  })
}

async function loadAssets() {
  loadingAssets.value = true
  try {
    assets.value = await AssetService.getAssetsByOrganizationIdAsync(organizationStore.organizationId)
    resolveThumbnails()
    // Per-asset counts only power the tablet/mobile row badges; the desktop list shows a description
    // instead, so skip the N summary calls there (the selected asset's summary still loads on select).
    if (!isDesktop.value) loadSummaries()
  } finally {
    loadingAssets.value = false
  }
}

async function loadSites() {
  try {
    sites.value = await SiteService.getSitesByOrganizationId(organizationStore.organizationId)
  } catch {
    sites.value = []
  }
}

async function loadBreakdown() {
  loadingBreakdown.value = true
  try {
    if (!assetIds.value.length) return
    breakdown.value = await AssetService.getOrganizationAnomalyBreakdownAsync(assetIds.value)
  } catch {
    breakdown.value = { total: 0, pending: 0, accepted: 0, rejected: 0 }
  } finally {
    loadingBreakdown.value = false
  }
}

// Sets an asset's thumbnail url, and mirrors it onto the detail hero if that asset is selected.
function setThumb(assetId, url) {
  imgLoaded[assetId] = false   // new src → shimmer until it decodes
  imageMap[assetId] = url
  if (url && selectedAsset.value?.id === assetId) detailImage.value = url
}

// True while a thumbnail should show the shimmer: either a url is present but the <img> hasn't
// decoded yet, or resolution is still in flight (no url and not yet settled on a placeholder).
function thumbLoading(assetId) {
  if (imageMap[assetId]) return !imgLoaded[assetId]
  return !thumbSettled[assetId]
}

function resolveThumbnails() {
  for (const a of activeAssets.value) resolveThumbnail(a)
}

// Resolve one asset's thumbnail: prefer its own overview image; if it has none (or it fails to
// load), fall back to the first frame of its current scan.
function resolveThumbnail(asset) {
  if (!asset || imageMap[asset.id]) return
  if (asset.imagePath && !thumbTried[asset.id]) {
    thumbTried[asset.id] = true
    organizationSasService.getSignedUrl(asset.imagePath)
      .then(url => url ? setThumb(asset.id, url) : fallbackFrameThumb(asset))
      .catch(() => fallbackFrameThumb(asset))
  } else {
    fallbackFrameThumb(asset)
  }
}

// Fallback thumbnail: a representative frame (~30% into the walk, front face for 360) of the asset's
// current scan. Best-effort — one extra (current-version + frames) fetch per image-less asset;
// guarded so it runs at most once each.
async function fallbackFrameThumb(asset) {
  if (!asset || frameTried[asset.id] || imageMap[asset.id]) return
  frameTried[asset.id] = true
  try {
    const mv = await ModelVersionService.getCurrentModelVersion(asset.id)
    const scanId = mv?.associatedScan || mv?.scans?.find(s => s.scanId)?.scanId
    if (!scanId) return
    const frames = await ModelVersionService.getFramesByScanId(scanId)
    const cover = pickCoverFrame(frames)
    if (!cover) return
    const url = await organizationSasService.getSignedUrl(cover.imagePath)
    if (url) setThumb(asset.id, url)
  } catch { /* leave placeholder */ }
  finally { thumbSettled[asset.id] = true }   // resolution done: image or placeholder, stop shimmering
}

// Lazy per-asset summaries (scan / anomaly / hotspot counts) for the table + detail. One call per
// asset — fine for tens of assets; a single aggregate endpoint would scale better later.
function loadSummaries() {
  for (const a of activeAssets.value) {
    if (summaryMap[a.id] !== undefined) continue
    summaryMap[a.id] = null   // in-flight
    DashboardDataService.getModelVersionSummaryById(a.id)
      .then(summary => { summaryMap[a.id] = summary || false })
      .catch(() => { summaryMap[a.id] = false })
  }
}

function anomalyBadgeText(assetId) {
  const s = summaryMap[assetId]
  if (s === undefined || s === null) return '…'
  if (s === false) return '—'
  return formatNumber(s.totalAnomalies)
}

function anomalyBadgeClass(assetId) {
  const s = summaryMap[assetId]
  if (!s || typeof s !== 'object') return 'badge-muted'
  return Number(s.totalAnomalies) > 0 ? 'badge-active' : 'badge-zero'
}

// A rendered thumbnail failed to load (broken/expired url) → drop it and try the frame fallback.
function onThumbError(assetId) {
  imageMap[assetId] = null
  imgLoaded[assetId] = false
  if (selectedAsset.value?.id === assetId) detailImage.value = null
  const asset = assets.value.find(a => a.id === assetId)
  if (asset) fallbackFrameThumb(asset)
}

// The detail hero image failed — same recovery, scoped to the selected asset.
function onDetailImageError() {
  if (selectedAsset.value) onThumbError(selectedAsset.value.id)
}

function selectAsset(asset) {
  assetStore.setAsset(asset)
  if (!showDetail.value) {
    router.push({ name: 'AssetLanding', params: { id: asset.id } })
    return
  }
  selectedAsset.value = asset
  detailImage.value = imageMap[asset.id] || null
  resolveThumbnail(asset)   // fills the thumbnail (own image or frame fallback) + the detail hero
  computeActionsDisabled(asset)
  if (summaryMap[asset.id] === undefined) {
    summaryMap[asset.id] = null
    DashboardDataService.getModelVersionSummaryById(asset.id)
      .then(summary => { summaryMap[asset.id] = summary || false })
      .catch(() => { summaryMap[asset.id] = false })
  }
}

// Mirrors AssetLandingView.getCurrentScan: the model/anomaly/dashboard actions are enabled only when
// the asset has a current model version whose every scan has finished processing. Duallo admins
// bypass so they can work with in-review scans. Guarded against a stale async result after a re-select.
async function computeActionsDisabled(asset) {
  const id = asset.id
  actionsDisabled.value = true   // gated by default until we confirm a ready model
  try {
    const mv = await ModelVersionService.getCurrentModelVersion(id)
    if (selectedAsset.value?.id !== id) return
    // No current model version at all → stay gated for EVERYONE, Duallo admins included
    // (matches the old asset landing page: the admin bypass only applied once a model existed).
    if (!mv) return
    const versionScanIds = (mv.scans?.length ? mv.scans.map(s => s.scanId) : [mv.associatedScan]).filter(Boolean)
    const allScans = await ModelVersionService.getScansByAssetId(id)
    if (selectedAsset.value?.id !== id) return
    const statusById = new Map((allScans || []).map(s => [s.id, s.status]))
    const allProcessed = versionScanIds.length > 0
      && versionScanIds.every(sid => statusById.get(sid) === SCAN_PROCESSING_SUCCESS)
    // A model exists: Duallo admins bypass the "all scans processed" gate so they can review pre-release.
    actionsDisabled.value = !allProcessed && !can(Permissions.DualloAdminView)
  } catch {
    actionsDisabled.value = true
  }
}

// Detail-panel asset actions → their routes.
const DETAIL_ROUTES = {
  model: 'AssetViewer',
  street: 'AssetViewer',
  review: 'AnomalyManagement',
  anomaly: 'AnomalyManagement',
  dashboard: 'Dashboard',
  upload: 'AssetAdmin',
  admin: 'AssetAdmin',
}

function handleDetailAction(key) {
  if (!selectedAsset.value) return
  // "Change asset" clears the selection back to the empty prompt.
  if (key === 'change') { selectedAsset.value = null; return }
  // "Generate report" opens the asset's Findings page with the report dialog already open.
  if (key === 'report') {
    assetStore.setAsset(selectedAsset.value)
    router.push({ name: 'AnomalyManagement', params: { id: selectedAsset.value.id }, query: { report: '1' } })
    return
  }
  const routeName = DETAIL_ROUTES[key]
  if (!routeName) return
  assetStore.setAsset(selectedAsset.value)
  const query = key === 'street' ? { view: 'street' } : {}
  router.push({ name: routeName, params: { id: selectedAsset.value.id }, query })
}

function navigateToCreateAsset() {
  showNewAsset.value = true
}

// A new asset was created in the dialog: reload the authoritative list, clear any filters that
// would hide it, then select it so it opens in the detail panel (or its landing page on mobile).
async function onAssetCreated(asset) {
  searchQuery.value = ''
  selectedSiteId.value = null
  await loadAssets()
  const created = assets.value.find(a => a.id === asset.id) || asset
  selectAsset(created)
}


function formatNumber(num) {
  if (num == null) return '0'
  return Number(num).toLocaleString()
}

function pct(value) {
  const total = breakdown.value.total || 0
  if (!total) return '0%'
  return `${(Number(value || 0) / total) * 100}%`
}

</script>

<style scoped>
/* ═══════════════ Desktop ═══════════════ */
.dk {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-sizing: border-box;
}

/* Heading row: "Assets" + count pill on the left, "New asset" on the right. */
.dk-head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex: 0 0 auto;
}

.dk-title-wrap { display: flex; align-items: center; gap: 10px; min-width: 0; }

.dk-title {
  font-size: clamp(1.4rem, calc(12.8px + 0.75vw), 1.7rem);
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.dk-count {
  flex: 0 0 auto;
  font-size: 0.78rem;
  font-weight: 700;
  color: #cfd8dc;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 2px 10px;
  line-height: 1.5;
}

.dk-add {
  flex: 0 0 auto;
  height: 40px;
  text-transform: none;
  font-weight: 700;
  border-radius: 10px;
  background: #1e2a31 !important;
  color: var(--duallo-accent) !important;
  border: 1px solid rgba(100, 255, 218, 0.28);
  box-shadow: none !important;
}
.dk-add:hover { background: #223138 !important; }

/* Search + filter on one row directly above the list. */
.dk-tools-row { display: flex; align-items: center; gap: 10px; flex: 0 0 auto; }

.dk-search { flex: 1 1 auto; }
.dk-search :deep(.v-field) { border-radius: 10px; background: rgba(0, 0, 0, 0.22); }
.dk-search :deep(.v-field__outline) { --v-field-border-opacity: 0.10; }

.dk-kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #8aa0aa;
  font-size: 0.75rem;
  font-family: ui-monospace, Menlo, Consolas, monospace;
}

.dk-sites {
  flex: 0 0 auto;
  height: 40px;
  text-transform: none;
  font-weight: 600;
  border-radius: 10px;
  color: #cfd8dc !important;
  background: rgba(0, 0, 0, 0.22) !important;
  border: 1px solid rgba(255, 255, 255, 0.10);
  box-shadow: none !important;
}
.dk-sites :deep(.v-btn__prepend .v-icon) { color: #f0b54a; }

.dk-body {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(320px, 380px) minmax(0, 1fr);
  gap: 20px;
}

/* Laptops at 125–150% OS scaling report a narrower CSS viewport: shrink the list
   column and gap so the detail panel keeps enough room and nothing overflows. */
@media (max-width: 1400px) {
  .dk-body { grid-template-columns: minmax(280px, 330px) minmax(0, 1fr); gap: 16px; }
}
@media (max-width: 1150px) {
  .dk-body { grid-template-columns: minmax(250px, 290px) minmax(0, 1fr); gap: 14px; }
}

/* Left column groups the tools + scrolling list. */
.dk-left {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Asset list (narrow left column: thumbnail + name + site) */
.dk-list {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 12px;
}

.dk-list::-webkit-scrollbar { width: 6px; }
.dk-list::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }

.dk-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: clamp(68px, calc(24px + 3.44vw), 90px);
  padding: 8px 12px 8px 8px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-left: 3px solid transparent;
  border-radius: 14px;
  overflow: hidden;   /* clip any child overflow at the card border */
  cursor: pointer;
  text-align: left;
  font: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.dk-row:hover { background: rgba(255, 255, 255, 0.05); }

.dk-row.is-selected {
  background: rgba(102, 199, 180, 0.08);
  border-color: rgba(102, 199, 180, 0.4);
  border-left-color: var(--duallo-accent-muted);
}

.dk-thumb {
  flex: 0 0 auto;
  width: clamp(64px, calc(24px + 3.125vw), 84px);
  height: clamp(50px, calc(22px + 2.19vw), 64px);
  border-radius: 8px;
  overflow: hidden;
  background: #1c272c;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dk-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* YouTube-style shimmer while a thumbnail is resolving / decoding. */
.dk-thumb.is-loading,
.row-thumb {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  overflow: hidden;
  background: #1c272c;
  display: flex;
  align-items: center;
  justify-content: center;
}
.row-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

.row-thumb.is-loading {
  background-color: #1c272c;
  background-image: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0) 30%,
    rgba(255, 255, 255, 0.07) 50%,
    rgba(255, 255, 255, 0) 70%
  );
  background-size: 220% 100%;
  background-repeat: no-repeat;
  animation: thumb-shimmer 1.25s ease-in-out infinite;
}

@keyframes thumb-shimmer {
  from { background-position: 180% 0; }
  to { background-position: -80% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .dk-thumb.is-loading,
  .row-thumb.is-loading { animation: none; }
}

/* ───────────── Skeleton rows (asset list loading) ───────────── */
.sk {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}
.sk::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
  animation: sk-shimmer 1.25s ease-in-out infinite;
}
@keyframes sk-shimmer {
  100% { transform: translateX(100%); }
}
@media (prefers-reduced-motion: reduce) {
  .sk::after { animation: none; }
}

.sk-line { display: block; height: 0.9rem; }
.dk-row-skeleton,
.asset-row-skeleton { cursor: default; pointer-events: none; }
.dk-sk-thumb { flex: 0 0 auto; width: clamp(64px, calc(24px + 3.125vw), 84px); height: clamp(50px, calc(22px + 2.19vw), 64px); border-radius: 8px; }
.row-sk-thumb { flex: 0 0 auto; width: 48px; height: 48px; border-radius: 10px; }
.row-sk-badge { flex: 0 0 auto; width: 34px; height: 22px; border-radius: 999px; }

/* Anomaly-summary loading shimmer */
.sk-total { display: inline-block; width: 84px; height: 1.9rem; border-radius: 8px; }
.sk-summary-bar { display: block; width: 100%; height: 6px; border-radius: 999px; margin: 2px 0; }
.sk-legend-line { display: inline-block; flex: 1 1 0; height: 0.8rem; border-radius: 6px; }

.dk-row-main { flex: 1 1 auto; min-width: 0; overflow: hidden; display: flex; flex-direction: column; justify-content: center; gap: 4px; }
.dk-row-skeleton .dk-row-main { gap: 8px; }
.asset-row-skeleton .row-main { display: flex; flex-direction: column; justify-content: center; gap: 8px; min-width: 0; }

.dk-row-name {
  font-size: clamp(0.9rem, calc(11.2px + 0.25vw), 1rem);
  font-weight: 700;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dk-row-site {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  font-size: 0.72rem;
  color: #f0d19a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dk-row-site :deep(.v-icon) { flex: 0 0 auto; }

/* ═══════════════ Tablet / mobile ═══════════════ */
.assets-workspace {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
}

.assets-workspace.has-detail {
  display: grid;
  grid-template-columns: minmax(320px, 380px) 1fr;
  gap: 20px;
}

.master { display: flex; flex-direction: column; min-height: 0; gap: 14px; }

.master-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }

.master-title { font-size: 1.5rem; font-weight: 700; color: #fff; margin: 0; }

.add-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 999px;
  color: var(--duallo-accent-muted);
  border-color: rgba(102, 199, 180, 0.5);
}

.master-filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.master-search { flex: 1 1 160px; min-width: 0; }
.site-btn { flex: 0 0 auto; color: #cfd8dc; border-color: rgba(255, 255, 255, 0.14); }

.site-menu { background: #1f2c34; border: 1px solid rgba(255, 255, 255, 0.08); }

.anomaly-summary {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.summary-headline { display: flex; align-items: baseline; gap: 8px; min-width: 0; }
.summary-total { font-size: 1.7rem; font-weight: 800; color: var(--duallo-accent-muted); line-height: 1; font-variant-numeric: tabular-nums; }
.summary-caption { font-size: 0.9rem; font-weight: 600; color: #cfd8dc; }

.summary-review {
  display: inline-flex; align-items: center; gap: 2px;
  background: none; border: none; color: var(--duallo-accent-muted);
  font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer; white-space: nowrap;
}

.summary-bar { display: flex; height: 6px; border-radius: 999px; overflow: hidden; background: rgba(255, 255, 255, 0.06); }
.seg { height: 100%; }
.seg-pending { background: #f5a623; }
.seg-resolved { background: #4ade80; }
.seg-critical { background: #ef4444; }

.summary-legend { display: flex; flex-wrap: wrap; gap: 14px; }
.legend { display: inline-flex; align-items: center; gap: 6px; font-size: 0.78rem; color: #a9b8be; font-weight: 500; }
.dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
.dot-pending { background: #f5a623; }
.dot-resolved { background: #4ade80; }
.dot-critical { background: #ef4444; }

.asset-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
}

.asset-list::-webkit-scrollbar { width: 6px; }
.asset-list::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }

.asset-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.asset-row:hover { background: rgba(255, 255, 255, 0.05); }
.asset-row.is-selected { background: rgba(102, 199, 180, 0.08); border-color: rgba(102, 199, 180, 0.5); }

.row-main { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.row-site { font-size: 0.8rem; color: #8aa0aa; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.row-chevron { flex: 0 0 auto; }

/* Shared badge */
.row-badge {
  flex: 0 0 auto;
  min-width: 30px;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.badge-active { background: rgba(240, 166, 74, 0.18); color: #f0b968; border: 1px solid rgba(240, 166, 74, 0.4); }
.badge-zero { background: rgba(74, 222, 128, 0.16); color: #6ee7a0; border: 1px solid rgba(74, 222, 128, 0.35); }
.badge-muted { background: rgba(255, 255, 255, 0.05); color: #8aa0aa; border: 1px solid rgba(255, 255, 255, 0.08); }
.badge-pending { background: rgba(240, 166, 74, 0.18); color: #f0b968; border: 1px solid rgba(240, 166, 74, 0.4); }

/* Section label in the mobile asset list (Needs review / All assets). */
.list-section {
  flex: 0 0 auto;
  padding: 8px 2px 2px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.list-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 16px;
  color: #8aa0aa;
  font-size: 0.9rem;
}

.list-state p { margin: 0; }
</style>
